import type { InspectionBinBounds } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionBinBounds'
import { inspectionExtremum } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionExtremum'
import type { InspectionLayerKey } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionLayers'
import type {
  InspectionFinding,
  InspectionResult,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionResultSchema'
import { inspectionProvenance } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionResultSchema'
import {
  inspectionSelectionBounds,
  validInspectionBounds,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionSelection'
import { useCallback, useMemo, useState } from 'react'
import type {
  GeckoComponentDataFrame,
  GeckoComponentShape,
  GeckoFeature,
  GeckoMarkupPrimitive,
} from 'types'
import { GeckoComponentRect } from 'types'

export function useInspectionViewSelection(
  result: InspectionResult,
  cdf: GeckoComponentDataFrame | undefined,
  values: ArrayLike<number> | undefined,
  componentId: string,
  setLayerKey: (key: InspectionLayerKey) => void
) {
  const initialFinding = result.findings?.find(
    (finding) => finding.id === result.selected_finding_id
  )
  const initialBounds =
    initialFinding?.bin_bounds ??
    inspectionProvenance(result).bin_bounds ??
    undefined
  const [selection, setSelection] = useState<InspectionBinBounds | undefined>(
    initialBounds
  )
  const [focus, setFocus] = useState<
    'whole' | 'minimum' | 'maximum' | 'selection'
  >(
    initialBounds &&
      (!result.presentation?.focus || result.presentation.focus === 'whole')
      ? 'selection'
      : (result.presentation?.focus ?? 'whole')
  )
  const extreme = useMemo(
    () =>
      cdf && values && (focus === 'minimum' || focus === 'maximum')
        ? inspectionExtremum(
            values,
            cdf.data.binning.x_bin_count,
            focus,
            inspectionProvenance(result).bin_bounds
          )
        : undefined,
    [cdf, values, focus, result]
  )
  const activeBounds = useMemo(
    () =>
      focus === 'selection'
        ? selection
        : extreme
          ? {
              x_start: extreme.x,
              x_end: extreme.x + 1,
              y_start: extreme.y,
              y_end: extreme.y + 1,
            }
          : undefined,
    [focus, selection, extreme]
  )
  const focusRect = useMemo(() => {
    if (
      !cdf ||
      !activeBounds ||
      !validInspectionBounds(
        activeBounds,
        cdf.data.binning.x_bin_count,
        cdf.data.binning.y_bin_count
      )
    )
      return undefined
    const x = cdf.data.geometry.start.x.plus(
      cdf.data.binning.bin_size.width.scale(activeBounds.x_start)
    )
    const y = cdf.data.geometry.start.y.plus(
      cdf.data.binning.bin_size.height.scale(activeBounds.y_start)
    )
    return GeckoComponentRect.CreateFromXYs(
      componentId,
      x.num,
      y.num,
      x.plus(
        cdf.data.binning.bin_size.width.scale(
          activeBounds.x_end - activeBounds.x_start
        )
      ).num,
      y.plus(
        cdf.data.binning.bin_size.height.scale(
          activeBounds.y_end - activeBounds.y_start
        )
      ).num,
      x.unit,
      y.unit
    )
  }, [cdf, componentId, activeBounds])
  const handleFinding = useCallback(
    (finding: InspectionFinding) => {
      setSelection(finding.bin_bounds)
      setLayerKey('thickness')
      setFocus('selection')
    },
    [setLayerKey]
  )
  const handleSelected = useCallback(
    (
      selected:
        | GeckoComponentShape
        | GeckoMarkupPrimitive
        | GeckoFeature
        | undefined
    ) => {
      if (selected === undefined) {
        setSelection(undefined)
        setFocus('whole')
        return
      }
      if (
        !cdf ||
        !(selected instanceof GeckoComponentRect) ||
        selected.componentID !== componentId
      )
        return
      const bounds = inspectionSelectionBounds(
        {
          xStart: cdf.data.geometry.start.x.num,
          yStart: cdf.data.geometry.start.y.num,
          binWidth: cdf.data.binning.bin_size.width.toNumber(
            cdf.data.geometry.start.x.unit
          ),
          binHeight: cdf.data.binning.bin_size.height.toNumber(
            cdf.data.geometry.start.y.unit
          ),
          columns: cdf.data.binning.x_bin_count,
          rows: cdf.data.binning.y_bin_count,
        },
        {
          xStart: selected.start.x.convertTo(cdf.data.geometry.start.x.unit)
            .num,
          xEnd: selected.end.x.convertTo(cdf.data.geometry.start.x.unit).num,
          yStart: selected.start.y.convertTo(cdf.data.geometry.start.y.unit)
            .num,
          yEnd: selected.end.y.convertTo(cdf.data.geometry.start.y.unit).num,
        }
      )
      if (bounds) {
        setSelection(bounds)
        setFocus('selection')
      }
    },
    [cdf, componentId]
  )
  return {
    focus,
    setFocus,
    activeBounds,
    extreme,
    focusRect,
    handleFinding,
    handleSelected,
  }
}
